import type { HelpGuide } from '../types';

export const advanceGuide: HelpGuide = {
  key: 'advance',
  title: 'เงินทดรองจ่าย (Employee Advance)',
  subtitle: 'เมนู: ลูกหนี้ / เจ้าหนี้ ▸ เงินทดรองจ่าย',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/advance/คู่มือการใช้งาน_เงินทดรองจ่าย_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “เงินทดรองจ่าย” ใช้คุมวงจรการเบิกเงินทดรองจ่ายของพนักงาน (Employee Advance) ตั้งแต่ ขอเบิก → อนุมัติ → จ่ายเงิน → เคลียร์ตามใบเสร็จ → คืนเงินส่วนต่าง ครบทั้งวงจร โดยบันทึกบัญชีลูกหนี้พนักงาน (เงินทดรองจ่าย บัญชี 10110005) ทุกขั้นตอนแบบอัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'หลักการบันทึกบัญชี: จ่ายเงินทดรอง → Dr เงินทดรองจ่าย (ลูกหนี้พนักงาน) / Cr เงินสด-ธนาคาร · เคลียร์ตามใบเสร็จ → Dr ค่าใช้จ่ายจริง + ภาษีซื้อ / Cr เงินทดรองจ่าย (+ Cr ภาษีหัก ณ ที่จ่าย ถ้ามี) · พนักงานคืนส่วนต่าง → Dr เงินสด / Cr เงินทดรองจ่าย · ตัดจำหน่ายยอดค้าง → Dr ค่าใช้จ่าย / Cr เงินทดรองจ่าย',
        },
        { kind: 'para', text: 'หน้าจอแบ่งรายการเป็น 6 แท็บตามสถานะ:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ทั้งหมด — ', text: 'รายการเงินทดรองทั้งหมด ไม่กรองสถานะ (แท็บเริ่มต้น)' },
            {
              lead: 'คำขอรออนุมัติ — ',
              text: 'รายการที่ส่งขออนุมัติแล้ว รอผู้ตรวจสอบ/ผู้อนุมัติพิจารณา',
            },
            { lead: 'อนุมัติแล้ว รอจ่าย — ', text: 'อนุมัติเรียบร้อย รอฝ่ายการเงินจ่ายเงินออก' },
            { lead: 'จ่ายแล้ว รอเคลียร์ — ', text: 'จ่ายเงินให้พนักงานแล้ว รอนำใบเสร็จมาเคลียร์' },
            {
              lead: 'เกินกำหนดเคลียร์ — ',
              text: 'เลยกำหนดเคลียร์แล้วยังไม่เคลียร์ (ต้องติดตามด่วน)',
            },
            { lead: 'เคลียร์แล้ว — ', text: 'เคลียร์ครบหรือถูกตัดจำหน่ายแล้ว (รายการปิดบัญชี)' },
          ],
        },
        {
          kind: 'image',
          src: '/help/advance/01-overview.png',
          caption:
            'ภาพรวม — การ์ด KPI (คงค้าง/เกินกำหนด/รออนุมัติ/เคลียร์แล้ว), แถบอายุเงินทดรองค้างเคลียร์ และตารางรายการ 6 แท็บ',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบนสุด:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'คงค้างยังไม่เคลียร์ — ',
              text: 'ยอดเงินทดรองที่จ่ายไปแล้วยังไม่เคลียร์ พร้อมจำนวนใบและค่าเฉลี่ยต่อใบ',
            },
            {
              lead: 'เกินกำหนดเคลียร์ — ',
              text: 'จำนวนใบที่เลยกำหนดเคลียร์ (แสดงสีแดงถ้ามี) + ยอดรวมที่ต้องติดตามด่วน',
            },
            { lead: 'คำขอรออนุมัติ — ', text: 'จำนวนใบและยอดรวมที่รอการอนุมัติ' },
            {
              lead: 'เคลียร์แล้วเดือนนี้ — ',
              text: 'ยอดที่เคลียร์ปิดบัญชีในเดือนนี้ + จำนวนใบที่ปิดแล้ว',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้ KPI มีแถบ “อายุเงินทดรองค้างเคลียร์” เป็นกราฟแท่งแยกตามช่วงอายุ (0–7 / 8–14 / 15–21 / 22–30 / เกิน 30 วัน) พร้อมยอดเงินและจำนวนใบแต่ละช่วง และข้อความนโยบาย: ต้องเคลียร์ภายใน 7 วัน · เกิน 30 วันต้องชี้แจง ผจก. การเงิน',
        },
        {
          kind: 'para',
          text: 'ปุ่มมุมขวาบน: เลือกงวด · นำเข้า (Import จากไฟล์ Excel) · ส่งออก (Export รายการที่เห็นเป็น .xlsx) · + ขอเบิกเงินทดรอง (ปุ่มสีแดง สร้างคำขอใหม่) ใต้แท็บมีแถวตัวกรอง: ค้นหา (เลขที่ใบเบิก/ชื่อพนักงาน/รายละเอียด) · วัตถุประสงค์ · แผนก · วิธีจ่าย (โอนธนาคาร/เงินสด) · ช่วงวันที่ (จากวันที่–ถึงวันที่)',
        },
        {
          kind: 'para',
          text: 'ตารางหลักมีคอลัมน์: เลขที่ใบเบิก (AV-ปีเดือน-เลขรัน) · วันที่ขอ · พนักงาน/แผนก · วัตถุประสงค์ (จุดสี) · รายละเอียด · ยอดขอ · อนุมัติ · จ่ายจริง · กำหนดเคลียร์ · อายุ · สถานะ · การกระทำ ปุ่มในคอลัมน์ “การกระทำ” จะเปลี่ยนตามสถานะของแถว (พิจารณา / จ่ายเงิน / เคลียร์ / ตัดจำหน่าย / ดูสรุป)',
        },
      ],
    },
    {
      id: 'request',
      heading: '3. การขอเบิกเงินทดรองจ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + ขอเบิกเงินทดรอง ระบบจะออกเลขที่ใบเบิก (AV-ปีเดือน-เลขรัน) ให้อัตโนมัติเมื่อบันทึก ขั้นแรกเลือก วัตถุประสงค์ในการเบิก จากกล่องสี 8 แบบ ได้แก่ เดินทางในประเทศ · เดินทางต่างประเทศ · ออกหน้างาน/ติดตั้งสินค้า · รับรองลูกค้า · อบรม/สัมมนา · งานแสดงสินค้า · จัดซื้อพิเศษ · อื่นๆ',
        },
        {
          kind: 'para',
          text: 'จากนั้นกรอก: พนักงานผู้ขอเบิก (จำเป็น) · สาขา · หน่วยงาน (Cost Center) · รายละเอียดภารกิจ (จำเป็น) · วันที่ขอเบิก · วันที่กลับ (อ้างอิง) · กำหนดเคลียร์ภายใน (ระบบตั้งค่าเริ่มต้นเป็น +7 วัน) หากพนักงานมีเงินทดรองค้างอยู่หรือยอดเกินวงเงินที่กำหนด ระบบจะแสดงคำเตือนสีเหลือง (เตือนเท่านั้น ไม่บล็อกการบันทึก)',
        },
        {
          kind: 'para',
          text: 'ตาราง “ประมาณการค่าใช้จ่าย” ให้ใส่รายการย่อยได้ (เช่น ค่าเดินทาง / ค่าที่พัก / ค่าอาหาร / เบ็ดเตล็ด) กดปุ่ม + เพิ่มรายการ เพื่อเพิ่มบรรทัด ระบบรวมยอดเป็น “รวมทั้งสิ้น (ขอเบิก)” อัตโนมัติ ด้านล่างมีแถบนโยบายสีเหลืองย้ำเงื่อนไข แล้วกด ส่งขออนุมัติ',
        },
        {
          kind: 'image',
          src: '/help/advance/02-request-modal.png',
          caption:
            'ขอเบิกเงินทดรองจ่าย — เลือกวัตถุประสงค์ 8 สี, ผู้ขอเบิก/สาขา/หน่วยงาน, ตารางประมาณการค่าใช้จ่าย และนโยบายเคลียร์ภายใน 7 วัน',
        },
      ],
    },
    {
      id: 'approve',
      heading: '4. การพิจารณาอนุมัติคำขอ',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บ คำขอรออนุมัติ กดปุ่ม พิจารณา ที่แถวคำขอ ระบบเปิดแผงสายอนุมัติ แสดงไทม์ไลน์ 2 ขั้น (ผู้ตรวจสอบ → ผู้อนุมัติ) ตามเมทริกซ์อนุมัติของบริษัท พร้อมไฮไลต์ขั้นปัจจุบัน',
        },
        {
          kind: 'para',
          text: 'ผู้มีสิทธิในแต่ละขั้นกดปุ่มพิจารณาเพื่อเลือก อนุมัติ / ไม่อนุมัติ (Reject) / ส่งกลับแก้ไข (Return) การส่งกลับแก้ไขจะคืนเอกสารให้ผู้ขอปรับปรุงแล้วส่งใหม่ ทุกการตัดสินใจถูกบันทึกไว้ในประวัติการอนุมัติ และระบบบังคับหลักการแบ่งแยกหน้าที่ (ผู้ทำรายการ/ผู้ตรวจสอบ/ผู้อนุมัติ ต้องเป็นคนละคน)',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'หน้านี้ใช้ระบบอนุมัติกลาง (Approval Engine) ตัวเดียวกับเอกสารอื่นในระบบ ลำดับขั้นและผู้มีสิทธิตามตำแหน่งถูกกำหนดในเมทริกซ์อนุมัติของแต่ละบริษัท',
        },
        {
          kind: 'image',
          src: '/help/advance/03-approval-panel.png',
          caption:
            'สายอนุมัติ — ไทม์ไลน์ 2 ขั้น (ผู้ตรวจสอบ → ผู้อนุมัติ) พร้อมปุ่มพิจารณา อนุมัติ/ไม่อนุมัติ/ส่งกลับแก้ไข',
        },
      ],
    },
    {
      id: 'disburse',
      heading: '5. การจ่ายเงินทดรอง',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อคำขออนุมัติครบแล้วจะย้ายไปแท็บ อนุมัติแล้ว รอจ่าย กดปุ่ม จ่ายเงิน → ที่แถว ระบบเปิดหน้าต่างจ่ายเงินทดรอง แสดงเลขที่ใบเบิกและยอดที่จะจ่ายออก (Dr เงินทดรอง / Cr เงินสด)',
        },
        {
          kind: 'para',
          text: 'เลือก วิธีจ่าย ได้แก่ โอนธนาคาร (ค่าเริ่มต้น) / เงินสด / เช็ค แล้วกด จ่ายเงิน ระบบจะลงใบสำคัญจ่ายให้อัตโนมัติและเปลี่ยนสถานะเป็น “จ่ายแล้ว รอเคลียร์” พร้อมเริ่มนับอายุการค้างเคลียร์',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'หลักการแบ่งแยกหน้าที่: ผู้จ่ายเงินต้องไม่ใช่ผู้อนุมัติขั้นสุดท้าย — ระบบจะตรวจสอบและกันไว้',
        },
        {
          kind: 'image',
          src: '/help/advance/04-disburse-modal.png',
          caption:
            'จ่ายเงินทดรอง — เลขที่ใบเบิก, ยอดจ่าย (Dr เงินทดรอง/Cr เงินสด) และเลือกวิธีจ่าย (โอน/เงินสด/เช็ค)',
        },
      ],
    },
    {
      id: 'clear-receipt',
      heading: '6. การเคลียร์เงินทดรอง — รายการตามใบเสร็จ',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บ จ่ายแล้ว รอเคลียร์ (หรือ เกินกำหนดเคลียร์) กดปุ่ม เคลียร์ → ระบบเปิดหน้าจอเคลียร์เงินทดรองจ่าย ด้านบนมีกล่องสรุป 4 ช่อง: เงินทดรองที่รับ · ค่าใช้จ่ายจริง (รวม) · คืนบริษัท/บริษัทจ่ายเพิ่ม · วันที่เคลียร์',
        },
        {
          kind: 'para',
          text: 'ใส่รายการค่าใช้จ่ายตามใบเสร็จทีละบรรทัด แต่ละบรรทัดเลือกประเภทรายการได้ สำหรับรายการตามใบเสร็จ (ค่าใช้จ่ายจริง) ให้ระบุ: บัญชีค่าใช้จ่าย (6xxxxxxx) · รายละเอียด · เลขที่ใบเสร็จ · จำนวนเงิน และข้อมูลภาษี: ประเภทค่าใช้จ่าย · ประเภทเอกสาร · ภาษีซื้อ · เลขประจำตัวผู้เสียภาษีผู้ขาย · ชื่อผู้ขาย ระบบคำนวณ ฐานภาษี / VAT 7% / หัก ณ ที่จ่าย ให้',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'แนบหลักฐาน — ',
              text: 'กดปุ่มแนบไฟล์ใบเสร็จ (รูปภาพ/PDF) ระบบช่วยอ่านข้อมูลจากใบเสร็จมาเติมให้ (จำเป็นต้องแนบเมื่อยอด ≥ 500 บาท หรือเป็นภาษีซื้อที่ขอคืนได้)',
            },
            {
              lead: 'เบี้ยเลี้ยง/ค่าน้ำมัน (Per Diem/Mileage) — ',
              text: 'เปลี่ยนประเภทรายการเป็นเบี้ยเลี้ยง/ระยะทาง คำนวณจาก อัตรา × จำนวนวัน/กม. โดยไม่มี VAT/ใบเสร็จ ส่วนที่เกินเพดานจะถูกตั้งสถานะเป็นเงินได้ที่ต้องเสียภาษี (ภ.ง.ด.1)',
            },
          ],
        },
        {
          kind: 'para',
          tone: 'muted',
          text: 'หากบันทึกเลขที่ใบเสร็จซ้ำ ระบบจะเตือนและให้ระบุเหตุผลเพื่อยืนยันบันทึกซ้ำ (มีการบันทึกประวัติไว้)',
        },
        {
          kind: 'image',
          src: '/help/advance/05-clear-receipt.png',
          caption:
            'เคลียร์เงินทดรองจ่าย — รายการตามใบเสร็จแบบรับรู้ภาษี (ค่าใช้จ่าย/ประเภทเอกสาร/ผู้ขาย, VAT 7% และหัก ณ ที่จ่าย)',
        },
      ],
    },
    {
      id: 'clear-summary',
      heading: '7. การเคลียร์เงินทดรอง — สรุปคืนเงินและส่งบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อใส่รายการครบ ด้านล่างมีกล่องสรุปคืนเงินขนาดใหญ่ เปรียบเทียบเงินทดรองที่รับกับค่าใช้จ่ายจริง: ถ้าใช้จ่ายน้อยกว่า จะขึ้น “พนักงานต้องคืนเงินส่วนต่างให้บริษัท” (สีเขียว ลูกศรลง) ถ้าใช้จ่ายมากกว่า จะขึ้น “บริษัทต้องจ่ายเพิ่มให้พนักงาน” (สีแดง ลูกศรขึ้น) พร้อมยอดเงินตัวใหญ่และคำแนะนำการจัดการ',
        },
        {
          kind: 'para',
          text: 'ก่อนส่ง ระบบตรวจเงื่อนไขอัตโนมัติ (เช่น ภาษีซื้อที่ขอคืนต้องมีใบกำกับภาษีเต็มรูป + เลขผู้เสียภาษีผู้ขาย, รายการที่หัก ณ ที่จ่ายต้องมีเลขผู้เสียภาษี, รายการ ≥ 500 บาทต้องแนบหลักฐาน) ถ้าไม่ครบจะแสดงป้ายแดงและปุ่มส่งจะยังกดไม่ได้ เมื่อครบแล้วกด ส่งเคลียร์ให้บัญชี ระบบลงใบสำคัญและปรับสถานะเป็น “เคลียร์แล้ว” (หรือ “เคลียร์บางส่วน” ถ้ายังไม่ครบ)',
        },
        {
          kind: 'image',
          src: '/help/advance/06-clear-summary.png',
          caption:
            'สรุปคืนเงิน — กล่อง “พนักงานต้องคืนเงินส่วนต่าง” และการ์ดรายงานก่อนส่งเคลียร์ให้บัญชี',
        },
      ],
    },
    {
      id: 'writeoff',
      heading: '8. การตัดจำหน่ายเงินทดรอง',
      blocks: [
        {
          kind: 'para',
          text: 'กรณีพนักงานไม่นำใบเสร็จมาเคลียร์ หรือมียอดค้างที่ติดตามคืนไม่ได้ ที่แท็บ จ่ายแล้ว รอเคลียร์ / เกินกำหนดเคลียร์ ให้กดปุ่ม ตัดจำหน่าย ระบบเปิดหน้าต่างตัดจำหน่ายเงินทดรอง แสดงเลขที่ใบเบิกและยอดคงค้างที่จะถูกตัดเป็นค่าใช้จ่าย (Dr ค่าใช้จ่ายเบ็ดเตล็ด / Cr เงินทดรอง)',
        },
        {
          kind: 'para',
          text: 'ระบุ วันที่ตัดจำหน่าย และ เหตุผลการตัดจำหน่าย (จำเป็น) แล้วกด ตัดจำหน่าย ระบบลงใบสำคัญและเปลี่ยนสถานะเป็น “เคลียร์แล้ว” หากยอดที่ตัดต้องตามหักคืนจากเงินเดือน ระบบจะส่งต่อไปยังการ์ด “หักจากเงินเดือน” เพื่อให้ HR ดำเนินการต่อ',
        },
        {
          kind: 'image',
          src: '/help/advance/07-writeoff-modal.png',
          caption: 'ตัดจำหน่ายเงินทดรอง — ยอดคงค้าง, วันที่ตัดจำหน่าย และเหตุผลการตัดจำหน่าย',
        },
      ],
    },
    {
      id: 'secondary',
      heading: '9. การ์ดสรุปและรายงานเพิ่มเติม',
      blocks: [
        {
          kind: 'para',
          text: 'ใต้ตารางหลักมีการ์ดสรุปเสริมและการ์ดรายงานช่วยติดตามและประสานงานกับ HR:',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'พนักงานที่มีเงินทดรองค้างเคลียร์ — ',
              text: 'รวมยอดคงค้างรายพนักงาน (ชื่อ/แผนก/จำนวนใบ/ยอดคงค้าง/สถานะ) เรียงจากมากไปน้อย',
            },
            {
              lead: 'เงินทดรองตามวัตถุประสงค์ — ',
              text: 'ยอดเบิกแยกตามวัตถุประสงค์ พร้อมจำนวนใบและสัดส่วน (%)',
            },
            {
              lead: 'หักจากเงินเดือน (ตัดจำหน่าย) — ',
              text: 'รายการที่ตัดจำหน่ายแล้วรอ HR หักผ่าน Payroll (ไม่ลงบัญชีเพิ่ม) กดปุ่ม “ทำจ่ายแล้ว” เมื่อหักเสร็จ',
            },
            {
              lead: 'เบี้ยเลี้ยงส่วนเกิน (ภ.ง.ด.1) — ',
              text: 'ส่วนเบี้ยเลี้ยง/ค่าน้ำมันที่เกินเพดาน ส่งให้ HR รวมเป็นเงินได้พนักงาน (ไม่ลงบัญชีเพิ่ม ไม่หัก ณ ที่จ่ายตอนเคลียร์)',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/advance/08-secondary-cards.png',
          caption:
            'การ์ดสรุปรอง — พนักงานค้างเคลียร์, เงินทดรองตามวัตถุประสงค์, หักจากเงินเดือน และเบี้ยเลี้ยงส่วนเกิน (ภ.ง.ด.1)',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะของเงินทดรอง',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย', 'ปุ่มที่ทำได้'],
          rows: [
            ['รออนุมัติ', 'ส่งขออนุมัติแล้ว รอผู้ตรวจสอบ/ผู้อนุมัติพิจารณา', 'พิจารณา'],
            ['รอจ่าย', 'อนุมัติครบแล้ว รอฝ่ายการเงินจ่ายเงินออก', 'จ่ายเงิน →'],
            ['รอเคลียร์', 'จ่ายเงินแล้ว รอนำใบเสร็จมาเคลียร์', 'เคลียร์ → / ตัดจำหน่าย'],
            [
              'เกินกำหนด',
              'เลยกำหนดเคลียร์แล้วยังไม่เคลียร์ (ติดตามด่วน)',
              'เคลียร์ → / ตัดจำหน่าย',
            ],
            ['เคลียร์แล้ว', 'เคลียร์ครบหรือถูกตัดจำหน่ายแล้ว (ปิดบัญชี)', 'ดูสรุป'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมส่งเคลียร์ไม่ได้ (ปุ่มเป็นสีเทา)?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'มีรายการที่ยังไม่ครบเงื่อนไข เช่น ภาษีซื้อขอคืนต้องมีใบกำกับภาษีเต็มรูป + เลขผู้เสียภาษีผู้ขาย, รายการที่หัก ณ ที่จ่ายต้องมีเลขผู้เสียภาษี, หรือรายการ ≥ 500 บาทยังไม่ได้แนบหลักฐาน — ดูป้ายแดงที่ระบบแจ้งแล้วแก้ให้ครบ',
            },
          ],
        },
        { kind: 'para', text: 'พนักงานใช้จ่ายเกินเงินที่เบิกไป ทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'กล่องสรุปจะขึ้น “บริษัทต้องจ่ายเพิ่มให้พนักงาน” พร้อมยอด ระบบจะคำนวณส่วนต่างให้ในใบสำคัญเคลียร์',
            },
          ],
        },
        { kind: 'para', text: 'จ่ายเงินทดรองให้ไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'จ่ายได้เฉพาะรายการที่อนุมัติครบแล้ว และผู้จ่ายต้องไม่ใช่ผู้อนุมัติขั้นสุดท้าย',
            },
          ],
        },
        { kind: 'para', text: 'ตัดจำหน่ายแล้วยอดที่ค้างหายไปเลยหรือไม่?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่หาย — ถ้ายอดต้องตามหักคืนจากพนักงาน ระบบส่งไปการ์ด “หักจากเงินเดือน” ให้ HR หักผ่าน Payroll ต่อ',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'เงินทดรองจ่าย — ',
              text: 'เงินที่บริษัทจ่ายล่วงหน้าให้พนักงานไปใช้ในภารกิจ บันทึกเป็นลูกหนี้พนักงาน (บัญชี 10110005) จนกว่าจะเคลียร์',
            },
            {
              lead: 'เคลียร์ (Clear) — ',
              text: 'การนำใบเสร็จค่าใช้จ่ายจริงมาหักล้างเงินทดรอง พร้อมคืนหรือจ่ายเพิ่มส่วนต่าง',
            },
            {
              lead: 'กำหนดเคลียร์ — ',
              text: 'วันครบกำหนดที่พนักงานต้องนำใบเสร็จมาเคลียร์ (นโยบายภายใน 7 วัน)',
            },
            {
              lead: 'ตัดจำหน่าย (Write-off) — ',
              text: 'การตัดยอดเงินทดรองที่ค้างเป็นค่าใช้จ่าย เมื่อไม่นำใบเสร็จมาเคลียร์/ติดตามคืนไม่ได้',
            },
            {
              lead: 'เบี้ยเลี้ยง/ค่าน้ำมัน (Per Diem/Mileage) — ',
              text: 'ค่าใช้จ่ายแบบเหมาจ่ายคิดจากอัตรา × จำนวนวัน/กม. ไม่ต้องมีใบเสร็จ ส่วนเกินเพดานเสียภาษี (ภ.ง.ด.1)',
            },
            {
              lead: 'ภาษีซื้อ (VAT 7%) — ',
              text: 'ภาษีมูลค่าเพิ่มที่ขอคืนได้เมื่อมีใบกำกับภาษีเต็มรูป + เลขผู้เสียภาษีผู้ขายครบ',
            },
            {
              lead: 'หัก ณ ที่จ่าย (WHT) — ',
              text: 'ภาษีที่หักจากค่าบริการบางประเภทตอนเคลียร์ นำส่งสรรพากร (ภ.ง.ด.3/53)',
            },
            {
              lead: 'ภ.ง.ด.1 — ',
              text: 'แบบนำส่งภาษีเงินได้พนักงาน ใช้กับเบี้ยเลี้ยงส่วนเกินเพดานที่ถือเป็นเงินได้',
            },
          ],
        },
      ],
    },
  ],
};
